import { Link, MobilePage, useParams } from '@tbitop/mobile-app';
import { useQueryParamState } from '@tbitop/routing';
import { Modal } from '@tbitop/ui';
import { House, X } from 'phosphor-react';
import { useRef, useState } from 'react';
import { toSortierrichtung } from '../core/application/sorting';
import type { NeukundenObjektFragment } from '../infrastructure/graphql/__generated__/NeukundenObjektErstellenMutation';
import { useAufgabeFuerKampagneErstellenMutation } from '../infrastructure/graphql/AufgabeFuerKampagneErstellenMutation';
import { useAufgabeLoeschenMutation } from '../infrastructure/graphql/AufgabeLoeschenMutation';
import { Sortierrichtung } from '../infrastructure/graphql/AufgabenSeitenweiseQuery';
import { useNeueObjekteFuerKampagnen } from '../infrastructure/graphql/NeueObjekteFuerKampagneQuery';
import { Aufgabe } from '../ui/controller';
import { ObjektAnlegen } from '../ui/controller/objekt-anlegen';
import type { ObjektFilters, ObjektSorting } from '../ui/controller/objekt-filter-aktionsaufgabe-erstellen';
import { ObjektfilterAktionsAufgabeErstellen } from '../ui/controller/objekt-filter-aktionsaufgabe-erstellen';
import { ObjektListAktionAnlegen } from '../ui/controller/objekt-list/objekt-list-aktion-erstellen';

export const actionParam = 'action';
export const actionDetails = 'details';

const defaultFilterValues: Readonly<ObjektFilters> = {};
const defaultSorting: ObjektSorting = [];

export const AktionsaufgabeErstellen = () => {
  const { aufgabenId, kampagnenName } = useParams();

  const aufgabeDirektAbschliessenCanceldCallback = useRef<(() => void)| null>(null);

  const [filterValues, setFilterValues] = useQueryParamState(
    'filter',
    defaultFilterValues,
  );

  const [sorting, setSorting] = useQueryParamState<ObjektSorting>(
    'sort',
    defaultSorting,
  );

  const { data: objekte, loading, pagination, refetch } = useNeueObjekteFuerKampagnen(
    kampagnenName || '',
    filterValues,
    toSortierrichtung(sorting, Sortierrichtung),
  );

  const [isObjektAnlegenOpen, setIsObjektAnlegenOpen] = useState(false);
  const [isAufgabenDetailsOpen, setIsAufgabenDetailsOpen] = useState(false);
  const [detailAufgabenId, setDetailAufgabenId] = useState('');
  const [isRefetching, setIsRefetching] = useState(false);
  const { execute: executeAufgabeErstellen } = useAufgabeFuerKampagneErstellenMutation();
  const { execute: executeAufgabeLoeschen } = useAufgabeLoeschenMutation();

  function closeObjektAnlegenModal() {
    setIsObjektAnlegenOpen(false);
  }

  function onDirektAbschliessen(aufgabenId: string, direktAbschliessenCanceled: () => void) {
    setDetailAufgabenId(aufgabenId);
    setIsAufgabenDetailsOpen(true);
    aufgabeDirektAbschliessenCanceldCallback.current = direktAbschliessenCanceled;
  }

  function onDirektAbschliessenCanceled() {
    setIsAufgabenDetailsOpen(false);
    aufgabeDirektAbschliessenCanceldCallback.current?.();
  }

  function onAfterDirektAbschliessenAction() {
    aufgabeDirektAbschliessenCanceldCallback.current = null;
  }

  async function onAfterObjektCreated(objekt: NeukundenObjektFragment) {
    if (!aufgabenId) {
      return;
    }
    const neueAufgabe = await executeAufgabeErstellen({ aufgabenId: aufgabenId, objektId: objekt.id });
    const neueAufgabenId = neueAufgabe.data?.aufgabeFuerKampagneErstellen.id;
    closeObjektAnlegenModal();
    setFilterValues({
      suchbegriff: objekt.name,
    });
    onDirektAbschliessen(neueAufgabenId || '', () => {
      executeAufgabeLoeschen({ id: neueAufgabenId || '' });
    });
  }

  return (
    <MobilePage title="Aktionsaufgabe erstellen" onPullToRefresh={async () => {
      setIsRefetching(true);
      await refetch();
      setIsRefetching(false);
    }}>
      <div className="sticky top-0 z-50 bg-tds-color-organizer-90 pb-300">
        <div className="flex justify-between mb-3 gap-300">
          <Link href="/objekte" className="flex items-center">
            <House size={22}></House>
          </Link>
          <div className="flex items-center gap-1">
            <p><b>Vorlage: </b> {kampagnenName}</p>
            <Link href="/aktionsaufgabe">
              <X size={18} />
            </Link>
          </div>
        </div>
        <ObjektfilterAktionsAufgabeErstellen
          filterValues={filterValues}
          onFilterChange={filterValues => {
            const newValues = { ...filterValues };
            newValues.suchbegriff = filterValues.suchbegriff || '';
            setFilterValues(newValues);
          }}
          sorting={sorting}
          onSortChange={setSorting}
        />
        <div className="flex justify-end">
          <span className="cursor-pointer" onClick={() => {
            setIsObjektAnlegenOpen(true);
          }}>Neukunde?</span>
        </div>
      </div>


      <ObjektListAktionAnlegen aufgabenId={aufgabenId || ''} objekte={objekte} loading={loading && !isRefetching} pagination={pagination}
        onDirektAbschliessen={onDirektAbschliessen}/>
      <Modal isOpen={isObjektAnlegenOpen} onClose={closeObjektAnlegenModal} size="large">
        <ObjektAnlegen onClose={closeObjektAnlegenModal} onObjektCreated={onAfterObjektCreated} />
      </Modal>
      <Modal isOpen={isAufgabenDetailsOpen && !!detailAufgabenId} onClose={onDirektAbschliessenCanceled} size="large">
        {isAufgabenDetailsOpen && !!detailAufgabenId ? <Aufgabe aufgabenId={detailAufgabenId} onClose={onDirektAbschliessenCanceled} onAfterAction={onAfterDirektAbschliessenAction}/> : null}
      </Modal>
    </MobilePage>
  );
};
